
import React, { useMemo, useState, useRef, useCallback } from 'react';
import MarkdownRenderer from '../MarkdownRenderer';
import { WebSearchData } from '@/types/webSearch';
import { KeyContact } from '@/types/lead';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/resizable';
import { Pencil, Save, X, Bold, Heading, List, Link, Minus } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface WebSearchContentProps {
  searchData: WebSearchData | null;
  keyContacts?: KeyContact[];
  editable?: boolean;
  onSave?: (markdown: string) => void;
}

function buildContactsTable(contacts: KeyContact[]): string {
  const valid = contacts.filter(c => {
    if (!c.name || c.name === 'unknown') return false;
    if (c.name.length > 100) return false;
    if (/[\n\r]/.test(c.name)) return false;
    if (/[|*#]/.test(c.name)) return false;
    return true;
  });
  if (valid.length === 0) return '';

  const rows = valid.map(c => {
    const email = c.email ? `[${c.email}](mailto:${c.email})` : '—';
    const phone = c.phone || '—';
    const position = c.position || '—';
    return `| ${c.name} | ${position} | ${email} | ${phone} |`;
  });

  return `\n\n**Key Contacts (from Deep Research)**\n\n| Name | Position | Email | Phone |\n| --- | --- | --- | --- |\n${rows.join('\n')}\n\n`;
}

function injectContactsIntoSummary(summary: string, contactsTable: string): string {
  const staffHeadingRegex = /(#{1,6}.*(?:Staff|Team\s*Members).*)/i;
  const match = summary.match(staffHeadingRegex);

  if (match && match.index !== undefined) {
    const afterHeading = summary.slice(match.index + match[0].length);
    const nextHeadingMatch = afterHeading.match(/\n#{1,6}\s/);
    const insertPos = nextHeadingMatch && nextHeadingMatch.index !== undefined
      ? match.index + match[0].length + nextHeadingMatch.index
      : summary.length;

    const before = summary.slice(0, insertPos).replace(/\s+$/, '');
    const after = summary.slice(insertPos);
    return before + contactsTable + after;
  }

  return summary.replace(/\s+$/, '') + contactsTable;
}

function insertAtCursor(textarea: HTMLTextAreaElement, before: string, after: string = '') {
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const text = textarea.value;
  const selected = text.slice(start, end);
  const replacement = before + selected + after;
  const newValue = text.slice(0, start) + replacement + text.slice(end);
  
  // We need to return the new value and cursor position
  return {
    value: newValue,
    cursorStart: start + before.length,
    cursorEnd: start + before.length + selected.length,
  };
}

const MarkdownToolbar: React.FC<{
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  onValueChange: (value: string) => void;
}> = ({ textareaRef, onValueChange }) => {
  const applyFormat = useCallback((before: string, after: string = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const result = insertAtCursor(textarea, before, after);
    onValueChange(result.value);
    
    // Restore cursor after React re-render
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(result.cursorStart, result.cursorEnd);
    });
  }, [textareaRef, onValueChange]);

  const tools = [
    { icon: Bold, label: 'Bold', action: () => applyFormat('**', '**') },
    { icon: Heading, label: 'Heading', action: () => applyFormat('\n## ') },
    { icon: List, label: 'Bullet list', action: () => applyFormat('\n- ') },
    { icon: Link, label: 'Link', action: () => applyFormat('[', '](url)') },
    { icon: Minus, label: 'Divider', action: () => applyFormat('\n---\n') },
  ];

  return (
    <TooltipProvider>
      <div className="flex items-center gap-0.5 border border-border rounded-md p-0.5 bg-muted/30">
        {tools.map(({ icon: Icon, label, action }) => (
          <Tooltip key={label}>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={action}
              >
                <Icon className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">{label}</TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TooltipProvider>
  );
};

const WebSearchContent: React.FC<WebSearchContentProps> = ({ searchData, keyContacts, editable, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const content = useMemo(() => {
    if (!searchData) return null;

    const base =
      (searchData.summary && searchData.summary.trim()) ||
      (searchData.rawText && searchData.rawText.trim()) ||
      '';

    if (!base) return '';

    if (keyContacts && keyContacts.length > 0) {
      const table = buildContactsTable(keyContacts);
      if (table) return injectContactsIntoSummary(base, table);
    }

    return base;
  }, [searchData, keyContacts]);

  // Get raw summary for editing (without injected contacts table)
  const rawSummary = useMemo(() => {
    if (!searchData) return '';
    return (searchData.summary && searchData.summary.trim()) ||
      (searchData.rawText && searchData.rawText.trim()) || '';
  }, [searchData]);

  const handleStartEdit = () => {
    setEditValue(rawSummary);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue('');
  };

  const handleSave = () => {
    onSave?.(editValue);
    setIsEditing(false);
    setEditValue('');
  };

  if (content === null) {
    return <p className="text-muted-foreground py-2">No web search data available.</p>;
  }

  if (!content) {
    return <p className="text-muted-foreground py-2">No summary available yet. Try refreshing or check Sources.</p>;
  }

  if (isEditing) {
    // Compute preview with contacts injected
    const previewContent = (() => {
      if (keyContacts && keyContacts.length > 0) {
        const table = buildContactsTable(keyContacts);
        if (table) return injectContactsIntoSummary(editValue, table);
      }
      return editValue;
    })();

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <MarkdownToolbar textareaRef={textareaRef} onValueChange={setEditValue} />
          <div className="flex items-center gap-1.5">
            <Button variant="ghost" size="sm" onClick={handleCancel} className="h-7 text-xs gap-1">
              <X className="h-3.5 w-3.5" /> Cancel
            </Button>
            <Button size="sm" onClick={handleSave} className="h-7 text-xs gap-1">
              <Save className="h-3.5 w-3.5" /> Save
            </Button>
          </div>
        </div>
        <ResizablePanelGroup direction="horizontal" className="min-h-[300px] rounded-md border border-border">
          <ResizablePanel defaultSize={50} minSize={30}>
            <Textarea
              ref={textareaRef}
              value={editValue}
              onChange={e => setEditValue(e.target.value)}
              className="h-full min-h-[300px] resize-none rounded-none border-0 font-mono text-sm focus-visible:ring-0 focus-visible:ring-offset-0"
              placeholder="Write markdown here..."
            />
          </ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel defaultSize={50} minSize={30}>
            <div className="h-full overflow-auto p-4">
              <MarkdownRenderer content={previewContent} />
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    );
  }

  return (
    <div className="relative group">
      {editable && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleStartEdit}
          className="absolute top-0 right-0 h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity z-10"
          title="Edit summary"
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      )}
      <MarkdownRenderer content={content} />
    </div>
  );
};

export default WebSearchContent;
